import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { useListState } from '../../lib/hooks';
import { Icon, type IconName } from '../../components/Icon';
import { EmptyState, ErrorState, PageHead, Pager } from '../../components/ui';

interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  changes: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

interface AuditPage {
  items: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * أسماء الكيانات بالعربية.
 *
 * الاسم يُشتق في الخادم من اسم المتحكّم فيأتي إنجليزيًا. الترجمة هنا لا
 * هناك: تغيير النص المعروض لا يصح أن يغيّر ما هو مكتوب في سجل قديم.
 */
const ENTITY_LABELS: Record<string, string> = {
  accounts: 'العملاء',
  contacts: 'جهات الاتصال',
  leads: 'العملاء المحتملون',
  deals: 'الصفقات',
  activities: 'الأنشطة',
  quotations: 'عروض الأسعار',
  contracts: 'العقود',
  services: 'الخدمات',
  packages: 'الباقات',
  invoices: 'الفواتير',
  payments: 'التحصيلات',
  recurring: 'الاشتراكات الدورية',
  payables: 'المشتريات',
  cashAccounts: 'البنوك والخزائن',
  accounting: 'المحاسبة',
  hr: 'الموارد البشرية',
  team: 'الفريق',
  auth: 'الحساب',
  settings: 'الإعدادات',
  lookups: 'قوائم القيم',
  calendar: 'التقويم',
  mail: 'البريد',
  warRoom: 'غرفة العمليات',
  reports: 'التقارير',
};

const VERB_LABELS: Record<string, string> = {
  create: 'إنشاء',
  update: 'تعديل',
  remove: 'حذف',
  delete: 'حذف',
  login: 'تسجيل دخول',
  logout: 'تسجيل خروج',
  changePassword: 'تغيير كلمة المرور',
  invite: 'دعوة عضو',
  resend: 'إعادة دعوة',
  transfer: 'نقل الملكية',
  setStatus: 'تغيير الحالة',
  approve: 'اعتماد',
  markPaid: 'تسجيل الصرف',
  terminate: 'إنهاء خدمة',
  run: 'إصدار',
  runDue: 'إصدار المستحق',
  send: 'إرسال',
  decide: 'قرار',
  cancel: 'إلغاء',
  seed: 'إضافة القيم المقترحة',
  saveAttendance: 'حفظ الحضور',
  createRun: 'فتح دورة مرتب',
  resetPassword: 'إعادة ضبط كلمة المرور',
};

/**
 * لونُ العمليةِ وأيقونتُها.
 *
 * السجلُّ يُقرأ بالمسح لا بالحرف: الأخضرُ إنشاءٌ والأحمرُ حذفٌ والكهرمانيُّ
 * تعديل — فتُلمحُ الحادثةُ الخطِرةُ قبل أن تُقرأ.
 */
const VERB_KIND: Record<string, { kind: string; icon: IconName }> = {
  create: { kind: 'ok', icon: 'plus' },
  seed: { kind: 'ok', icon: 'plus' },
  createRun: { kind: 'ok', icon: 'plus' },
  approve: { kind: 'ok', icon: 'check' },
  decide: { kind: 'ok', icon: 'check' },
  markPaid: { kind: 'ok', icon: 'check' },
  run: { kind: 'ok', icon: 'check' },
  runDue: { kind: 'ok', icon: 'check' },
  update: { kind: 'warn', icon: 'edit' },
  setStatus: { kind: 'warn', icon: 'refresh' },
  saveAttendance: { kind: 'warn', icon: 'edit' },
  transfer: { kind: 'warn', icon: 'arrowRightLeft' },
  changePassword: { kind: 'warn', icon: 'settings' },
  resetPassword: { kind: 'warn', icon: 'settings' },
  remove: { kind: 'danger', icon: 'trash' },
  delete: { kind: 'danger', icon: 'trash' },
  terminate: { kind: 'danger', icon: 'x' },
  cancel: { kind: 'danger', icon: 'x' },
  login: { kind: 'brand', icon: 'user' },
  logout: { kind: 'neutral', icon: 'logout' },
  invite: { kind: 'brand', icon: 'send' },
  resend: { kind: 'brand', icon: 'send' },
  send: { kind: 'brand', icon: 'send' },
};

function describe(action: string): { entity: string; verb: string; kind: string; icon: IconName } {
  const [entity = '', verb = ''] = action.split('.');
  const k = VERB_KIND[verb] ?? { kind: 'neutral', icon: 'book' as IconName };
  return {
    entity: ENTITY_LABELS[entity] ?? entity,
    verb: VERB_LABELS[verb] ?? verb,
    kind: k.kind,
    icon: k.icon,
  };
}

/** أوّلُ حرفين من الاسم — صورةٌ رمزيّةٌ بلا صورة. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0] ?? '').join('') || '؟';
}

/** قيمةٌ مقروءةٌ بدل JSON خام. */
function readable(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'نعم' : 'لا';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * سجل التدقيق.
 *
 * قراءة فقط: لا زر حذف ولا تعديل في الشاشة ولا مسار لهما في الخادم. سجلّ
 * يُمحى منه سطر لا يُحتج به في خلاف، وهو الموضع الذي يُحتاج فيه أصلًا.
 */
export function AuditLog() {
  const { state, patch, page } = useListState();
  const [open, setOpen] = useState<string | null>(null);

  /*
   * البحثُ يُكتبُ بالعربيّةِ والعمليةُ مخزَّنةٌ بالإنجليزيّة.
   *
   * فتُترجَمُ الكلمةُ إلى مفتاحها قبل إرسالها: «فاتورة» تصيرُ invoices
   * و«حذف» تصيرُ remove — فيبحثُ الخادمُ فيما هو مكتوبٌ فعلًا. وما لا يُطابق
   * اسمًا معروفًا يُرسَلُ كما هو (يفيدُ في الـIP ومعرّفِ العنصر).
   */
  const rawQ = (state.q ?? '').trim();
  const translatedQ = rawQ
    ? (Object.keys(ENTITY_LABELS).find((k) => ENTITY_LABELS[k]!.includes(rawQ)) ??
       Object.keys(VERB_LABELS).find((k) => VERB_LABELS[k]!.includes(rawQ)) ??
       rawQ)
    : undefined;

  const query = { ...state, q: translatedQ, page, pageSize: 50 };

  const list = useQuery({
    queryKey: ['audit', query],
    queryFn: () => api<AuditPage>(`/audit${qs(query)}`),
  });

  const actors = useQuery({
    queryKey: ['audit', 'actors'],
    queryFn: () => api<Array<{ id: string; name: string }>>('/audit/actors'),
  });

  const rows = list.data?.items ?? [];
  const entities = [...new Set(Object.keys(ENTITY_LABELS))].sort();
  const hasFilter = Boolean(state.entity || state.actorId || state.from || state.to || rawQ);

  return (
    <div className="page">
      {/* الترويسةُ واللافتةُ والفلاترُ ثابتةٌ — يبدأُ التمريرُ من الأحداثِ تحتها. */}
      <div className="page-sticky">
      <PageHead title="سجل التدقيق" subtitle="من فعل ماذا، ومتى" />

      {/* لافتةٌ موجزة: عددُ الأحداثِ ووصفُ السجل */}
      <div className="aud-hero">
        <span className="aud-hero-ic">
          <Icon name="book" size={22} />
        </span>
        <div className="aud-hero-txt">
          <h2>أثرٌ لا يُمحى</h2>
          <p>يُسجَّل كلُّ تغييرٍ ناجحٍ في النظام. القراءةُ لا تُسجَّل، ولا تُحفَظ كلماتُ المرور.</p>
        </div>
        {list.data && (
          <div className="aud-hero-stat">
            <strong className="num">{list.data.total.toLocaleString('en-US')}</strong>
            <span>حدثًا مسجَّلًا</span>
          </div>
        )}
      </div>

      {/* شريطُ فلترةٍ مرتَّبٌ جنبًا إلى جنبٍ بعناوينَ صغيرةٍ فوقَ كلِّ حقل. */}
      <div className="filter-bar">
        <label className="filter-item filter-item-wide">
          <span className="filter-label">
            <Icon name="search" size={12} /> بحث
          </span>
          <input
            className="input"
            placeholder="عملية أو وحدة أو IP…"
            value={state.q ?? ''}
            onChange={(e) => patch({ q: e.target.value || undefined })}
          />
        </label>

        <label className="filter-item">
          <span className="filter-label">
            <Icon name="layers" size={12} /> الوحدة
          </span>
          <select
            className="select"
            value={state.entity ?? ''}
            onChange={(e) => patch({ entity: e.target.value || undefined })}
          >
            <option value="">كل الوحدات</option>
            {entities.map((e) => (
              <option key={e} value={e}>
                {ENTITY_LABELS[e]}
              </option>
            ))}
          </select>
        </label>

        <label className="filter-item">
          <span className="filter-label">
            <Icon name="user" size={12} /> المستخدم
          </span>
          <select
            className="select"
            value={state.actorId ?? ''}
            onChange={(e) => patch({ actorId: e.target.value || undefined })}
          >
            <option value="">كل المستخدمين</option>
            {actors.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>

        <label className="filter-item">
          <span className="filter-label">
            <Icon name="calendar" size={12} /> من تاريخ
          </span>
          <input
            className="input num"
            type="date"
            value={state.from ?? ''}
            onChange={(e) => patch({ from: e.target.value || undefined })}
          />
        </label>

        <label className="filter-item">
          <span className="filter-label">
            <Icon name="calendar" size={12} /> إلى تاريخ
          </span>
          <input
            className="input num"
            type="date"
            value={state.to ?? ''}
            onChange={(e) => patch({ to: e.target.value || undefined })}
          />
        </label>

        {hasFilter && (
          <button
            className="btn btn-sm filter-clear"
            onClick={() =>
              patch({
                entity: undefined,
                actorId: undefined,
                from: undefined,
                to: undefined,
                q: undefined,
              })
            }
          >
            <Icon name="x" size={14} /> مسح الفلاتر
          </button>
        )}
      </div>
      </div>

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <div className="card">
        {list.isLoading ? (
          <div className="card-body">
            <div className="skeleton" style={{ height: 320 }} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="لا أثر مسجَّل"
            text="يُسجَّل هنا كل تغيير يقع في النظام. القراءة لا تُسجَّل."
          />
        ) : (
          <div className="aud-list">
            {rows.map((r) => {
              const { entity, verb, kind, icon } = describe(r.action);
              const detail = (r.changes ?? {}) as Record<string, unknown>;
              const hasDetail = Object.keys(detail).length > 0;
              const isOpen = open === r.id;
              const actor = r.actorName ?? 'النظام';

              return (
                <div className={`aud-item${isOpen ? ' is-open' : ''}`} key={r.id}>
                  {/* خيطُ الزمنِ وأيقونةُ العملية */}
                  <span className={`aud-ic is-${kind}`}>
                    <Icon name={icon} size={15} />
                  </span>

                  <div className="aud-body">
                    <div className="aud-line">
                      <span className="aud-avatar">{initialsOf(actor)}</span>
                      <strong className="aud-actor">{actor}</strong>
                      <span className={`aud-verb is-${kind}`}>{verb}</span>
                      <span className="aud-entity">{entity}</span>
                      <span className="aud-time" title={dateTime(r.createdAt)}>
                        {relative(r.createdAt)}
                      </span>
                    </div>

                    <div className="aud-sub">
                      {r.actorEmail && (
                        <span className="num" dir="ltr">
                          {r.actorEmail}
                        </span>
                      )}
                      {r.ip && (
                        <span className="num" dir="ltr">
                          IP {r.ip}
                        </span>
                      )}
                      <span className="num" dir="ltr">
                        {dateTime(r.createdAt)}
                      </span>
                      {hasDetail && (
                        <button
                          type="button"
                          className="aud-more"
                          onClick={() => setOpen(isOpen ? null : r.id)}
                        >
                          <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={13} />
                          {isOpen ? 'إخفاء التفصيل' : 'التفصيل'}
                        </button>
                      )}
                    </div>

                    {isOpen && hasDetail && (
                      /*
                       * المحتوى منقّى في الخادم قبل الحفظ: كلمات المرور
                       * والرموز تُحجب هناك، فلا يصل إلى هنا سرّ أصلًا.
                       */
                      <div className="aud-detail">
                        {Object.entries(detail).map(([k, v]) => (
                          <div className="aud-change" key={k}>
                            <span className="aud-key num" dir="ltr">
                              {k}
                            </span>
                            <span className="aud-val">{readable(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {list.data && rows.length > 0 && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>

      <p className="tiny mute mt-3">
        السجل للقراءة فقط ولا يُعدَّل ولا يُحذف منه شيء. يُسجَّل ما نجح من العمليات دون
        المرفوض، ولا تُحفظ فيه كلمات المرور أو الرموز.
      </p>
    </div>
  );
}
